/**
 * Quiz image asset resolver.
 *
 * The redesigned quiz needs a handful of hero photos + profile stat icons. The
 * theme injects permanent URLs at runtime via `window.__QUIZ_ASSETS__` (wired in
 * `sections/react-quiz.liquid` from files uploaded to `/assets`). Until those land
 * we fall back to the Figma export URLs so development renders correctly.
 *
 * TODO(assets): upload the files to /assets + populate window.__QUIZ_ASSETS__,
 * then the Figma fallbacks below can be removed (they expire ~7 days after export).
 */
declare global {
  interface Window {
    __QUIZ_ASSETS__?: Record<string, string>;
  }
}

const FALLBACK: Record<string, string> = {
  "delivery-frozen-box": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/e57bc6362deb62bd6df62a75a2f59d2d8cca3d04_800x800.jpg?v=1780243419",
  "delivery-dog-with-box": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Dog_with_box_-_compressed_800x800.jpg?v=1788962077",
  "delivery-fridge-tubs": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Lokis_Packaging_purple_800x800.jpg?v=1788962267",
  "hiw-dog-cutout": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/hiw-dog-cutout.png?v=1790596188",
  "hiw-dog-eating": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/dog_eating_blue_background_800x800.jpg?v=1788962526",
  "risk-free-dogs-eating": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/dogs_eating_800x800.webp?v=1784733620",
  "recipe-vinnie": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/pop-recipe-vinnies-veggie-salmon.jpg",
  "recipe-boot": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/pop-recipe-boots-beef-bowl.jpg",
  "recipe-loki": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/pop-recipe-lokis-lovely-lamb.jpg",
  "recipe-tilly": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/pop-recipe-tillys-tasty-turkey.jpg",
  "recipe-sammy": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/pop-recipe-sammys-salmon-turkey.jpg",
  "hero-supplement-dog": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Boots_beef_bowl_dog.jpg?v=1779176467",
  "review-money-back-dog": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Vinnie_s_Veggie_Packed_Salmon_Supper_dog.jpg?v=1779176510",
  "hero-food": "https://www.figma.com/api/mcp/asset/3e245baf-9132-4ddd-9f37-d35f0cedb91c",
  "hero-profile-dog": "https://www.figma.com/api/mcp/asset/626dfece-fc62-4f10-9432-67618fcfe88c",
  "icon-weight": "https://www.figma.com/api/mcp/asset/0326790d-b3e2-44e9-a794-316a05f62f8f",
  "icon-ideal": "https://www.figma.com/api/mcp/asset/36ed1ada-cd56-454f-bf1a-0e6ab806413c",
  "icon-age": "https://www.figma.com/api/mcp/asset/970beecb-518f-48ca-bf43-72d7b7d5a3f7",
  "icon-treat": "https://www.figma.com/api/mcp/asset/580d3e36-151a-493b-a8af-bec09789c8ab",
  "icon-active": "https://www.figma.com/api/mcp/asset/49a04fcf-3c82-435d-95d6-44abb6846d99",
  "icon-food": "https://www.figma.com/api/mcp/asset/147d120b-ebd7-4ed1-b895-c3b4126c70a2",
  "icon-din-supplement": "/assets/quiz-ic-din-supplement.png",
  "supplement-salmon-oil": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/superfood-salmon-oil.jpg?v=1791905793",
  "supplement-seaweed": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/superfood-seaweed.jpg?v=1791905771",
  "supplement-green-lipped-mussel": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/superfood-green-lipped-mussel.jpg?v=1791905798",
  "supplement-turmeric": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/superfood-turmeric.jpg?v=1792748930",
  "supplement-ginger": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/superfood-ginger.jpg?v=1792748930",
  "supplement-chia": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/superfood-chia.jpg?v=1791905778",
  // activity choice icons (dog poses)
  "icon-coach-potato": "https://www.figma.com/api/mcp/asset/28b06d82-2aaa-45dd-a11f-3439e3a081d0",
  "icon-active-dog": "https://www.figma.com/api/mcp/asset/409fa89e-7e4f-4113-a479-7a9d3c6986ab",
  "icon-athlete": "https://www.figma.com/api/mcp/asset/c26a6ac7-bc95-4e2c-b4ff-64e0797e6838",
  "story-sam-loki": "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Sam_Loki_Garden.png?v=1786468228",
  "summary-story-video": "https://cdn.shopify.com/videos/c/o/v/a4c851133c404d3fbd358ef2d4969f02.mp4",
};

export function quizAsset(name: string): string {
  if (typeof window !== "undefined" && window.__QUIZ_ASSETS__?.[name]) {
    return window.__QUIZ_ASSETS__[name];
  }
  return FALLBACK[name] ?? "";
}
